import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Button from '../components/common/Button';
import { useAuth } from '../context/AuthContext';
import { getUserOrders, getDisplayUrl } from '../services/memoryService';
import logger from '../utils/logger';
import { LogIn, Home } from 'lucide-react';
import './OrdersPage.css';

function OrdersPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [displayUrls, setDisplayUrls] = useState({});

  const loadDisplayUrls = async (orders) => {
    const urlPromises = [];
    
    orders.forEach(order => {
      if (order.order_items) {
        order.order_items.forEach(item => {
          if (item.memories) {
            const memory = item.memories;
            const imageUrl = memory.figurine_url || memory.photo_url;
            if (imageUrl) {
              urlPromises.push(
                getDisplayUrl(imageUrl).then(displayUrl => ({
                  key: `${order.id}-${item.id}`,
                  displayUrl
                })).catch(error => {
                  console.error('Error getting display URL:', error);
                  return {
                    key: `${order.id}-${item.id}`,
                    displayUrl: imageUrl
                  };
                })
              );
            }
          }
        });
      }
    });

    const results = await Promise.all(urlPromises);
    const urlMap = {};
    results.forEach(result => {
      urlMap[result.key] = result.displayUrl;
    });
    setDisplayUrls(urlMap);
  };

  useEffect(() => {
    const loadOrders = async () => {
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const data = await getUserOrders();
        const ordersArray = Array.isArray(data) ? data : [];
        setOrders(ordersArray);
        await loadDisplayUrls(ordersArray);
      } catch (e) {
        console.error('Failed to load orders:', e);
        setError('Failed to load orders. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadOrders();
  }, [isAuthenticated, user?.id]);

  const formatDateTime = (iso) => {
    try {
      return new Date(iso).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      logger.warn('Error formatting date:', error);
      return iso;
    }
  };

  const formatCurrency = (amount, currency) => {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: (currency || 'AUD').toUpperCase(),
      }).format(Number(amount || 0));
    } catch (error) {
      logger.warn('Error formatting currency:', error);
      return `${amount} ${currency || 'AUD'}`;
    }
  };

  const renderShippingAddress = (address) => {
    if (!address || typeof address !== 'object') return null;
    const lines = [];
    if (address.name) lines.push(address.name);
    const street = [address.line1, address.line2].filter(Boolean).join(', ');
    if (street) lines.push(street);
    const cityStatePost = [address.city, address.state, address.postal_code]
      .filter(Boolean)
      .join(', ');
    if (cityStatePost) lines.push(cityStatePost);
    if (address.country) lines.push(address.country);
    if (lines.length === 0) return null;
    return (
      <div className="shipping-address">
        {lines.map((text, idx) => (
          <div key={idx}>{text}</div>
        ))}
      </div>
    );
  };

  if (!isAuthenticated) {
    return (
      <div className="orders-page">
        <Navbar />
        <div className="content">
          <div className="orders-container">
            <div className="orders-empty">
              <p>Please log in to view your orders.</p>
              <Button
                variant="primary"
                className="orders-primary-btn"
                onClick={() => navigate('/auth')}
                icon={LogIn}
              >
                Log in
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="orders-page">
        <Navbar />
        <div className="content">
          <div className="orders-container">
            <div className="orders-loading">
              <div className="loading-spinner"></div>
              <p>Loading your orders...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="orders-page">
      <Navbar />
      <div className="content">
        <div className="orders-container">
          <div className="page-header">
            <h1>Your Orders</h1>
            <p>Track your order status and details</p>
          </div>

          {error && <div className="error-message">{error}</div>}

          {!orders || orders.length === 0 ? (
            <div className="orders-empty">
              <img src="/empty_image.png" alt="No orders" className="empty-state-image" loading="lazy" />
              <p>You don't have any orders yet.</p>
              <Button
                variant="primary"
                className="orders-primary-btn"
                onClick={() => navigate('/home')}
                icon={Home}
              >
                Go to My Studio
              </Button>
            </div>
          ) : (
            <div className="orders-list">
              {orders.map((order) => {
                const itemCount = order.order_items?.length || 0;
                return (
                  <div key={order.id} className="order-card">
                    <div className="order-header">
                      <div className="order-status-row">
                        <span className="order-status-label">Status:</span>
                        <span
                          className={`order-status status-${(order.status || 'pending').toLowerCase()}`}
                        >
                          {(order.status || 'pending').toUpperCase()}
                        </span>
                      </div>
                      <div className="order-info-row">
                        <span className="order-date">{formatDateTime(order.created_at)}</span>
                        <div className="order-total">
                          {formatCurrency(order.total_amount, order.currency)}
                        </div>
                      </div>
                    </div>

                    {itemCount > 0 && (
                      <div className="order-items-grid">
                        {order.order_items.map((item) => {
                          const memory = item.memories || {};
                          const originalImageUrl = memory.figurine_url || memory.photo_url;
                          const displayUrlKey = `${order.id}-${item.id}`;
                          const imageUrl = displayUrls[displayUrlKey] || originalImageUrl || '/empty_image.png';
                          return (
                            <div className="order-item-card" key={item.id}>
                              <div className="order-item-thumb">
                                <img
                                  src={imageUrl}
                                  alt={memory.title || 'Tiniego'}
                                  loading="lazy"
                                  onError={(e) => {
                                    e.target.src = '/empty_image.png';
                                  }}
                                />
                              </div>
                              <div className="order-item-title">{memory.title || 'Tiniego'}</div>
                              <div className="order-item-meta">
                                <span>Qty {item.quantity} | Height {(Number(item.height) || Number(memory.height) || 75)}mm</span>
                                <span>{formatCurrency(item.price, order.currency)}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {renderShippingAddress(order.shipping_address) && (
                      <div className="order-shipping">
                        <div className="shipping-title">Shipping</div>
                        {renderShippingAddress(order.shipping_address)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default OrdersPage;
